/**
 * React Query hooks for Commerce > Orders.
 *
 * - useOrders(filters)         GET /api/v1/commerce/orders (infinite, cursor)
 * - useOrderFilters()          GET /api/v1/commerce/orders/filters
 * - useOrder(orderId)          GET /api/v1/commerce/orders/{orderId}
 * - useShipOne / useShipBulk   POST /ship and /ship-bulk (admin)
 * - useCreateOrder             POST /            (editor: Add Order)
 * - useUpdateOrder             PUT  /{orderId}   (editor)
 * - useDeleteOrder             DELETE /{orderId} (admin)
 *
 * RBAC day-1 reuses the Faire-Sage Entra groups; see hasFaireViewer /
 * hasFaireAdmin in useFaireRuns. The Commerce Lambda mirrors that mapping.
 */

import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { apiDelete, apiGet, apiPost, apiPut } from './api';
import { API_BASE } from './apiBase';

export const COMMERCE_API_BASE = `${API_BASE}/v1/commerce/orders`;

export type OrderStatus =
  | 'received'
  | 'ready_to_sync'
  | 'sync_in_progress'
  | 'in_sage'
  | 'allocated'
  | 'shipped'
  | 'invoiced'
  | 'delivered'
  | 'cancelled'
  | 'manual_draft';

export type ShipStatus =
  | 'not_ready'
  | 'eligible'
  | 'pushed'
  | 'failed'
  | 'suppressed'
  | 'n_a';

// Display labels for order status. Single source of truth; mirrors COCKPIT.md
// section 4.1. Record<OrderStatus, string> causes a compile error on missing values.
export const ORDER_STATUS_LABELS: Record<OrderStatus, string> = {
  received: 'Received',
  ready_to_sync: 'Ready to Sync',
  sync_in_progress: 'Syncing',
  in_sage: 'In Sage',
  allocated: 'Allocated',
  shipped: 'Shipped',
  invoiced: 'Invoiced',
  delivered: 'Delivered',
  cancelled: 'Cancelled',
  manual_draft: 'Manual Draft',
};

// Display labels for ship_status. Single source of truth so the orders grid
// pill and the order-detail row never drift. `n_a` reads "Not needed" (api#97
// relabel: Cockpit owes no tracking push for orders Faire already shipped).
export const SHIP_STATUS_LABELS: Record<ShipStatus, string> = {
  not_ready: 'Not Ready',
  eligible: 'Eligible',
  pushed: 'Pushed',
  failed: 'Failed',
  suppressed: 'Suppressed',
  n_a: 'Not needed',
};

export type OrderRow = {
  order_id: string;
  source_channel: string;
  source_order_id: string;
  brand: string;
  partner_id: string | null;
  partner_name: string | null;
  partner_type: 'retailer' | 'guest' | 'private_label' | 'broker' | 'direct';
  order_date: string;
  required_ship_date: string | null;
  shipped_at: string | null;
  delivered_at: string | null;
  status: OrderStatus;
  ship_status: ShipStatus;
  total_amount: string | null;
  currency: string;
  sage_so_number: string | null;
  sage_invoice_no: string | null;
  tracking_number: string | null;
  carrier: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  // Number of notes on this order. Powers the sticky-note presence
  // indicator on the row's Actions strip (filled icon when > 0,
  // outlined when zero). Populated by the list endpoint via a LEFT JOIN
  // to commerce_order_notes; single-row reads return 0.
  notes_count: number;
};

export type ListOrdersResponse = {
  orders: OrderRow[];
  next_cursor: string | null;
  total_count: number | null;
  limit: number;
};

export type OrdersFilters = {
  q?: string;
  from?: string;
  to?: string;
  channel?: string[];
  brand?: string[];
  partner?: string[];
  status?: string[];
  ship_status?: string[];
  // Encoded as "<column>:asc|desc". The Lambda whitelists the column.
  // Undefined means use the backend default (order_date DESC, order_id DESC).
  sort?: string;
  limit?: number;
};

export type FilterOption = { value: string; label: string; sublabel?: string };
export type FilterOptions = {
  channels: FilterOption[];
  brands: FilterOption[];
  partners: FilterOption[];
  statuses: FilterOption[];
  ship_statuses: FilterOption[];
};

function buildOrdersQs(f: OrdersFilters, cursor: string | null): string {
  const p = new URLSearchParams();
  if (f.q) p.set('q', f.q);
  if (f.from) p.set('from', f.from);
  if (f.to) p.set('to', f.to);
  if (f.channel && f.channel.length > 0) p.set('channel', f.channel.join(','));
  if (f.brand && f.brand.length > 0) p.set('brand', f.brand.join(','));
  if (f.partner && f.partner.length > 0) p.set('partner', f.partner.join(','));
  if (f.status && f.status.length > 0) p.set('status', f.status.join(','));
  if (f.ship_status && f.ship_status.length > 0) p.set('ship_status', f.ship_status.join(','));
  if (f.sort) p.set('sort', f.sort);
  if (f.limit) p.set('limit', String(f.limit));
  if (cursor) p.set('cursor', cursor);
  return p.toString();
}

export function useOrders(filters: OrdersFilters = {}) {
  const query = useInfiniteQuery<ListOrdersResponse, Error>({
    queryKey: ['commerce-orders', filters],
    queryFn: ({ pageParam }) =>
      apiGet<ListOrdersResponse>(
        `${COMMERCE_API_BASE}?${buildOrdersQs(filters, pageParam as string | null)}`,
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor,
    staleTime: 15_000,
  });

  const rows: OrderRow[] = (query.data?.pages ?? []).flatMap((p) => p.orders);
  const totalCount = query.data?.pages[0]?.total_count ?? null;
  return {
    rows,
    totalCount,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
    fetchNextPage: () => query.fetchNextPage(),
    error: query.error,
    refetch: () => query.refetch(),
  };
}

export function useOrderFilters() {
  const query = useQuery<FilterOptions, Error>({
    queryKey: ['commerce-orders-filters'],
    queryFn: () => apiGet<FilterOptions>(`${COMMERCE_API_BASE}/filters`),
    staleTime: 5 * 60_000,
  });
  return {
    options: query.data,
    isLoading: query.isLoading,
    error: query.error,
  };
}

export function useOrder(orderId: string | null | undefined) {
  const query = useQuery<OrderRow & { metadata: unknown }, Error>({
    queryKey: ['commerce-order', orderId],
    queryFn: () =>
      apiGet<OrderRow & { metadata: unknown }>(
        `${COMMERCE_API_BASE}/${encodeURIComponent(orderId ?? '')}`,
      ),
    enabled: !!orderId,
  });
  return query;
}

export type ShipResult = {
  order_id: string;
  ok: boolean;
  ship_status: ShipStatus;
  error?: string;
};

export function useShipOne() {
  const qc = useQueryClient();
  return useMutation<ShipResult, Error, string>({
    mutationFn: (orderId: string) =>
      apiPost<ShipResult>(`${COMMERCE_API_BASE}/${encodeURIComponent(orderId)}/ship`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['commerce-orders'] });
    },
  });
}

export type BulkShipResponse = { results: ShipResult[] };

export function useShipBulk() {
  const qc = useQueryClient();
  return useMutation<BulkShipResponse, Error, string[]>({
    mutationFn: (orderIds: string[]) =>
      apiPost<BulkShipResponse>(`${COMMERCE_API_BASE}/ship-bulk`, { order_ids: orderIds }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['commerce-orders'] });
    },
  });
}

export type CreateOrderBody = {
  brand: string;
  partner_id?: string | null;
  partner_name?: string | null;
  partner_type?: OrderRow['partner_type'];
  order_date?: string;
  total_amount?: number | null;
  note?: string | null;
};

export function useCreateOrder() {
  const qc = useQueryClient();
  return useMutation<
    { order_id: string; source_order_id: string },
    Error,
    CreateOrderBody
  >({
    mutationFn: (body) => apiPost(`${COMMERCE_API_BASE}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['commerce-orders'] });
    },
  });
}

export function useUpdateOrder() {
  const qc = useQueryClient();
  return useMutation<
    { order_id: string; updated: boolean },
    Error,
    { orderId: string; body: Partial<CreateOrderBody> & { status?: OrderStatus } }
  >({
    mutationFn: ({ orderId, body }) =>
      apiPut(`${COMMERCE_API_BASE}/${encodeURIComponent(orderId)}`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['commerce-orders'] });
    },
  });
}

export function useDeleteOrder() {
  const qc = useQueryClient();
  return useMutation<{ order_id: string; deleted: boolean }, Error, string>({
    mutationFn: (orderId) =>
      apiDelete(`${COMMERCE_API_BASE}/${encodeURIComponent(orderId)}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['commerce-orders'] });
    },
  });
}

// Triggers a browser download of the formatted XLSX. The endpoint returns a
// base64-encoded body (API Gateway constraint) so we re-decode here.
export async function downloadOrdersXlsx(filters: OrdersFilters): Promise<void> {
  const qs = buildOrdersQs(filters, null);
  const url = `${COMMERCE_API_BASE}/export${qs ? `?${qs}` : ''}`;
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const dispo = res.headers.get('content-disposition') ?? '';
  const m = dispo.match(/filename="([^"]+)"/);
  const filename = m?.[1] ?? `commerce-orders.xlsx`;
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}

export function formatMoney(v: string | number | null | undefined): string {
  if (v == null) return '--';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '--';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n);
}

// Display the user-facing order number. Faire IDs come in as `bo_xxx`
// lowercase; the packing slip and Faire UI both show them uppercase without
// the prefix. Strip and uppercase to match.
export function formatOrderNumber(sourceOrderId: string | null | undefined): string {
  if (!sourceOrderId) return '';
  const s = String(sourceOrderId);
  const stripped = s.replace(/^(bo_|r_)/i, '');
  return stripped.toUpperCase();
}

// Returns the Faire brand-portal deep-link for a Faire order, or null for
// any other channel or when the order ID is absent.
export function faireOrderUrl(
  sourceChannel: string,
  sourceOrderId: string | null | undefined,
): string | null {
  if (sourceChannel !== 'faire' || !sourceOrderId) return null;
  return `https://www.faire.com/brand-portal/orders/${sourceOrderId}/order-fulfilment`;
}

// --- Sync status + sync action ---------------------------------------------

export type SyncStatus = {
  status_counts: Record<string, number>;
  last_poller_run: {
    run_id: string;
    started_at: string | null;
    finished_at: string | null;
    status: string;
    orders_pulled: number;
    orders_in_pipefile: number;
    orders_written: number;
    s3_pipefile_key: string | null;
  } | null;
  sage_mirror: {
    last_updated: string | null;
    invoice_header_rows: number;
  };
  shipment_scanner: {
    last_eligible_at: string | null;
    last_push_at: string | null;
    pushback_rows: number;
  };
  // Canonical fields per COCKPIT.md section 4.3. These replace the
  // legacy fields above for what the Sync Status modal renders. The
  // legacy fields are still in the response for backward compatibility
  // until any other caller is migrated off.
  poller_last_run_at: string | null;
  runner_last_pipefile_at: string | null;
  sage_mirror_last_refreshed_at: string | null;
  orders_pending_match: number;
  orders_ready_to_sync: number;
  shipments_eligible: number;
  last_sage_invoice_count_24h: number;
};

export function useSyncStatus(enabled = true) {
  return useQuery<SyncStatus, Error>({
    queryKey: ['commerce-sync-status'],
    queryFn: () => apiGet<SyncStatus>(`${COMMERCE_API_BASE}/sync-status`),
    enabled,
    staleTime: 15_000,
  });
}

export type SyncResult = {
  ok: boolean;
  run_id: string | null;
  orders_in_pipefile: number;
  orders_pulled: number;
  orders_skipped: number;
  rows_advanced: number;
  s3_pipefile_key: string | null;
  file_url: string | null;
};

export function useRunSync() {
  const qc = useQueryClient();
  return useMutation<SyncResult, Error, void>({
    mutationFn: () => apiPost<SyncResult>(`${COMMERCE_API_BASE}/sync`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['commerce-orders'] });
      void qc.invalidateQueries({ queryKey: ['commerce-sync-status'] });
    },
  });
}

// --- Detail / partner enrichment --------------------------------------------

export type PartnerEnrichment = {
  sage_customer_no: string;
  customer_name: string | null;
  address_line_1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  phone: string | null;
  terms_code: string | null;
  salesperson_no: string | null;
  last_invoice_date: string | null;
  lifetime_invoice_count: number;
  ytd_invoice_count: number;
  ytd_freight: string | null;
};

export type OrderDetail = OrderRow & {
  metadata: unknown;
  line_items: unknown[] | null;
  // True when line_items was populated from metadata.lines (raw Faire eaches)
  // rather than the typed commerce_orders_line_items table.
  _line_items_from_metadata?: boolean;
  partner: PartnerEnrichment | null;
  external_url: string | null;
  // Typed address columns added in migration 0007. Null for legacy rows; see
  // fillAddressesFromMetadata for the metadata fallback.
  ship_to: ChannelAddress | null;
  bill_to: ChannelAddress | null;
};

// The API may not populate line_items even though the poller writes lines into
// metadata.lines on every pass. Derive line_items from metadata.lines so the
// order detail page shows line items without waiting for an API-side fix.
export function mergeLineItems(data: OrderDetail): OrderDetail {
  if (Array.isArray(data.line_items) && data.line_items.length > 0) return data;
  const m =
    data.metadata && typeof data.metadata === 'object'
      ? (data.metadata as Record<string, unknown>)
      : {};
  const lines = Array.isArray(m.lines) && m.lines.length > 0 ? m.lines : null;
  return lines ? { ...data, line_items: lines, _line_items_from_metadata: true } : data;
}

// Normalize a raw Faire AddressBlock (state_code/country_code) or an already-
// normalized ChannelAddress (state/country) from metadata into ChannelAddress.
// Returns null when the block is missing or has no meaningful content.
function normalizeMetadataAddress(block: unknown): ChannelAddress | null {
  if (!block || typeof block !== 'object') return null;
  const b = block as Record<string, unknown>;
  const line1 = typeof b.line1 === 'string' ? b.line1 : null;
  const city = typeof b.city === 'string' ? b.city : null;
  const state =
    typeof b.state === 'string' ? b.state
    : typeof b.state_code === 'string' ? b.state_code
    : null;
  const zip = typeof b.zip === 'string' ? b.zip : null;
  if (!line1 && !city && !state && !zip) return null;
  return {
    company_name: typeof b.company_name === 'string' ? b.company_name : null,
    name: typeof b.name === 'string' ? b.name : null,
    line1,
    line2: typeof b.line2 === 'string' ? b.line2 : null,
    city,
    state,
    zip,
    country:
      typeof b.country === 'string' ? b.country
      : typeof b.country_code === 'string' ? b.country_code
      : null,
    phone: typeof b.phone === 'string' ? b.phone : null,
    email: typeof b.email === 'string' ? b.email : null,
  };
}

// For legacy rows where the typed ship_to/bill_to columns are NULL, fall back
// to the raw Faire envelope stored in metadata.
export function fillAddressesFromMetadata(data: OrderDetail): OrderDetail {
  // != null catches BOTH null and undefined: the api detail response does not
  // include ship_to/bill_to keys at all yet, so they arrive undefined. The
  // original !== null guard treated undefined as "already populated" and the
  // fallback never ran (caught on dev 2026-06-05: MGGUQU2KX9 showed no
  // address cards despite full metadata blocks).
  if (data.ship_to != null && data.bill_to != null) return data;
  const m =
    data.metadata && typeof data.metadata === 'object'
      ? (data.metadata as Record<string, unknown>)
      : {};
  const ship_to = data.ship_to ?? normalizeMetadataAddress(m.ship_to);
  const bill_to = data.bill_to ?? normalizeMetadataAddress(m.bill_to);
  if (ship_to == null && bill_to == null && data.ship_to === undefined && data.bill_to === undefined) {
    return { ...data, ship_to: null, bill_to: null };
  }
  if (ship_to === data.ship_to && bill_to === data.bill_to) return data;
  return { ...data, ship_to: ship_to ?? null, bill_to: bill_to ?? null };
}

export function useOrderDetail(orderId: string | null | undefined) {
  return useQuery<OrderDetail, Error, OrderDetail>({
    queryKey: ['commerce-order', orderId],
    queryFn: () =>
      apiGet<OrderDetail>(`${COMMERCE_API_BASE}/${encodeURIComponent(orderId ?? '')}`),
    enabled: !!orderId,
    select: (data) => fillAddressesFromMetadata(mergeLineItems(data)),
  });
}

// --- Notes ------------------------------------------------------------------

export type OrderNote = {
  note_id: number;
  order_id: string;
  author_email: string;
  body: string;
  created_at: string;
};

export function useOrderNotes(orderId: string | null | undefined, enabled = true) {
  return useQuery<{ notes: OrderNote[] }, Error>({
    queryKey: ['commerce-order-notes', orderId],
    // The commerce notes endpoint returns { data: [...] }; expose it as { notes }
    // so the shared NotesModal binding (which reads .notes) renders the rows.
    queryFn: async () => {
      const r = await apiGet<{ data: OrderNote[] }>(
        `${COMMERCE_API_BASE}/${encodeURIComponent(orderId ?? '')}/notes`,
      );
      return { notes: r.data ?? [] };
    },
    enabled: !!orderId && enabled,
  });
}

export function useAddNote() {
  const qc = useQueryClient();
  return useMutation<{ note_id: number }, Error, { orderId: string; body: string }>({
    mutationFn: ({ orderId, body }) =>
      apiPost(`${COMMERCE_API_BASE}/${encodeURIComponent(orderId)}/notes`, { body }),
    onSuccess: (_data, { orderId }) => {
      void qc.invalidateQueries({ queryKey: ['commerce-order-notes', orderId] });
      void qc.invalidateQueries({ queryKey: ['commerce-order-audit', orderId] });
      // Refresh the orders list so the grid notes-count badge updates.
      void qc.invalidateQueries({ queryKey: ['commerce-orders'] });
    },
  });
}

// --- Audit ------------------------------------------------------------------

export type AuditEvent = {
  audit_id: number;
  occurred_at: string;
  actor_email: string;
  action: string;
  outcome: string;
  detail: unknown;
};

export function useOrderAudit(orderId: string | null | undefined, enabled = true) {
  return useQuery<{ events: AuditEvent[] }, Error>({
    queryKey: ['commerce-order-audit', orderId],
    queryFn: () =>
      apiGet<{ events: AuditEvent[] }>(
        `${COMMERCE_API_BASE}/${encodeURIComponent(orderId ?? '')}/audit`,
      ),
    enabled: !!orderId && enabled,
  });
}

// --- Match candidates -------------------------------------------------------

export type MatchCandidate = {
  sage_customer_no: string;
  customer_name: string;
  address_line_1: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  score: number;
  source: 'existing_mapping' | 'name_match' | 'name_state_match';
  // Populated after api#104 ships: the specific Sage Ship-To that matched the
  // order's ship address. Optional so existing callers are unaffected.
  matched_ship_to?: ChannelAddress | null;
};

// Faire (or other channel) address block carried alongside match candidates
// so the modal can show the operator exactly what address came in on the
// inbound order. The server normalizes line1/line2/state_code/etc. into this
// shape regardless of the source channel's native field names.
export type ChannelAddress = {
  company_name: string | null;
  name: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  phone: string | null;
  email: string | null;
};

export type MatchCandidatesResponse = {
  partner_name: string | null;
  ship_to_state: string | null;
  retailer_id: string | null;
  bill_to: ChannelAddress | null;
  ship_to: ChannelAddress | null;
  candidates: MatchCandidate[];
};

export function useMatchCandidates(orderId: string | null | undefined, enabled = true) {
  return useQuery<MatchCandidatesResponse, Error>({
    queryKey: ['commerce-match-candidates', orderId],
    queryFn: () =>
      apiGet<MatchCandidatesResponse>(
        `${COMMERCE_API_BASE}/${encodeURIComponent(orderId ?? '')}/match-candidates`,
      ),
    enabled: !!orderId && enabled,
  });
}

export function useApplyMatch() {
  const qc = useQueryClient();
  return useMutation<
    { order_id: string; sage_customer_no: string; customer_name: string | null },
    Error,
    { orderId: string; sage_customer_no: string }
  >({
    mutationFn: ({ orderId, sage_customer_no }) =>
      apiPost(`${COMMERCE_API_BASE}/${encodeURIComponent(orderId)}/match`, {
        sage_customer_no,
      }),
    onSuccess: (_d, { orderId }) => {
      void qc.invalidateQueries({ queryKey: ['commerce-orders'] });
      void qc.invalidateQueries({ queryKey: ['commerce-order', orderId] });
      void qc.invalidateQueries({ queryKey: ['commerce-order-audit', orderId] });
    },
  });
}

// Sage customer free-text search (reuses the existing faire-sage endpoint so
// match modal users can fall back from ranked candidates to a free search).
export type SageCustomerHit = {
  CustomerNo: string;
  CustomerName: string;
  AddressLine1: string | null;
  City: string | null;
  State: string | null;
  ZipCode: string | null;
  TelephoneNo: string | null;
};

export function useSageCustomerSearch(q: string, state: string | null, enabled = true) {
  return useQuery<{ customers: SageCustomerHit[] }, Error>({
    queryKey: ['sage-customer-search', q, state],
    queryFn: () => {
      const p = new URLSearchParams();
      p.set('q', q);
      if (state) p.set('state', state);
      p.set('limit', '20');
      return apiGet<{ customers: SageCustomerHit[] }>(
        `${API_BASE}/v1/commerce/customers/search?${p.toString()}`,
      );
    },
    enabled: enabled && q.trim().length >= 2,
  });
}
